import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import type { ImportResult } from '../types';

interface CompleteStepProps {
  loading: boolean;
  result: ImportResult | null;
  entityLabel: string;
  closeLabel: string;
  onImportAnother: () => void;
  onClose: () => void;
}

export default function CompleteStep({
  loading,
  result,
  entityLabel,
  closeLabel,
  onImportAnother,
  onClose,
}: CompleteStepProps) {
  if (loading || !result) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-3" />
        <p className="text-gray-600 font-medium">Importing {entityLabel}…</p>
        <p className="text-sm text-gray-400 mt-1">This may take a moment for large files.</p>
      </div>
    );
  }

  const hasErrors = (result.errors?.length || 0) > 0;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">Step 5: Complete</h4>
        <p className="text-gray-600 text-sm">Import finished. Review the summary below.</p>
      </div>

      <div
        className={`p-4 rounded-lg flex items-start gap-3 border ${
          result.success && !hasErrors
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-amber-50 border-amber-200 text-amber-900'
        }`}
      >
        {result.success && !hasErrors ? (
          <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" />
        )}
        <div>
          <h5 className="font-bold">{result.message}</h5>
          <p className="mt-1 text-sm">
            Imported: {result.imported ?? 0}
            {typeof result.skipped === 'number' ? ` · Skipped: ${result.skipped}` : ''}
            {typeof result.total === 'number' ? ` · Total rows: ${result.total}` : ''}
          </p>
        </div>
      </div>

      {hasErrors && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-56">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Row</th>
                <th className="text-left px-3 py-2 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {result.errors!.map((err, idx) => (
                <tr key={`${err.row}-${idx}`}>
                  <td className="px-3 py-2">{err.row}</td>
                  <td className="px-3 py-2 text-red-700">{err.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-primary">
          {closeLabel}
        </button>
        <button type="button" onClick={onImportAnother} className="btn-secondary">
          Import Another File
        </button>
      </div>
    </div>
  );
}
